import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { LayoutQuery } from '../../../layout/state/layout.query';
import { LayoutService } from '../../../layout/state/layout.service';
import { TILES } from '../../constants/reference.constants';
import { DocumentItem, UploadedFile } from '../../models/DocumentItem ';
import { ReferenceService } from '../../services/reference.service';
import { DocumentGridDialogComponent } from '../document-grid-dialog/document-grid-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { DocumentArchiveWithFilesModel } from '../../models/DocumentArchiveWithFilesModel';
import { AuthenticationService } from '../../../authentication/state/authentication.service';
import { AuthenticationHttpService } from '../../../authentication/services/authentication-http.service';
import { LogService } from '../../../core/logger/services/log.service';
import { UserService } from '../../../user/services/user.service';

@Component({
  selector: 'app-accreditation-kuwait',
  templateUrl: './accreditation-kuwait.component.html',
  styleUrl: './accreditation-kuwait.component.scss'
})
export class AccreditationKuwaitComponent implements OnInit {

  headingLabel: string = 'VIEW';
  documents: DocumentArchiveWithFilesModel[] = [];
  displayedColumns: string[] = ['name', 'uploadDocuments', 'uploadedFiles', 'actions', 'status'];
  AccreditationKwtForm!: FormGroup;
  canSave = false;
  parentStatus: string = '';
  selectedUserRoles!: string[];
  tiles: any;
  loading = false;
  matchedTile: any;
  isLoader: boolean = false;

  constructor(
    private referenceService: ReferenceService,
    public layoutQuery: LayoutQuery,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private layoutService: LayoutService,
    private toaster: ToastrService,
    private authenticationService: AuthenticationService,
    private authHttpService: AuthenticationHttpService,
    private userService: UserService,
    private logService: LogService,
  ) { }

  ngOnInit(): void {
    this.getUserTiles();

    const currentPath = this.router.url;
    this.authHttpService.getUserInfo().subscribe(result => {
      if (result) {
        const userId = result.sub;
      }
    });
    this.matchedTile = TILES.find(tile => tile.viewLink && currentPath.startsWith(tile.viewLink));
    if (this.matchedTile) {
      this.headingLabel = this.matchedTile.viewLabel || 'VIEW';
    }

    this.AccreditationKwtForm = this.fb.group({
      uploadDocuments: this.fb.array([])
    });

    this.route.queryParams.subscribe((params) => {
      const categoryId = +params['categoryId'];
      if (categoryId) {
        this.loadDocuments(categoryId);
      }
    });
  }

  get documentsArray(): FormArray {
    return this.AccreditationKwtForm.get('uploadDocuments') as FormArray;
  }

  onSave(): void {
    if (!this.AccreditationKwtForm.valid) {
      console.error('Form is invalid!');
      return;
    }
    this.isLoader = true;
    const docs = this.AccreditationKwtForm.value.uploadDocuments;

    const documentItems: DocumentItem[] = docs.map((doc: any) => ({
      referenceId: doc.id,
      name: doc.name,
      uploadedFiles: doc.uploadedFiles
    }));

    this.referenceService.saveDocuments(documentItems).subscribe({
      next: () => {
        this.toaster.success('Documents saved!');
        this.isLoader = false;
        this.ngOnInit();
        this.documentsArray.controls.forEach(control => {
          const docGroup = control as FormGroup;
          const uploadedFiles = docGroup.get('uploadedFiles')?.value ?? [];
          const savedFiles = uploadedFiles.map((file: any) => ({
            ...file,
            saved: true
          }));
          docGroup.get('uploadedFiles')?.setValue(savedFiles);
        });
        this.checkCanSave();
      },
      error: err => {
        console.error('Error saving:', err);
        this.isLoader = false;
      }
    });
  }

  loadDocuments(categoryId: number): void {
    this.isLoader = true;
    this.referenceService.getDocumentsWithFiles(categoryId).subscribe({
      next: docs => {
        this.documents = docs.map(doc => {
          const uploadedFiles = doc.uploadedFiles || [];
          const groupedByDoc: { [docId: number]: any[] } = {};
          uploadedFiles.forEach(f => {
            if (!groupedByDoc[f.documentId]) groupedByDoc[f.documentId] = [];
            groupedByDoc[f.documentId].push(f);
          });
          const majorFiles: any[] = [];
          Object.values(groupedByDoc).forEach(files => {
            const groupedByMajor: { [major: number]: any[] } = {};
            files.forEach(f => {
              const major = Math.trunc(Number(f.versionNumber));
              if (!groupedByMajor[major]) groupedByMajor[major] = [];
              groupedByMajor[major].push(f);
            });
            Object.values(groupedByMajor).forEach(group => {
              const latestVersion = Math.max(...group.map(f => Number(f.versionNumber)));
              const latestFile = group.find(f => Number(f.versionNumber) === latestVersion)!;
              majorFiles.push(latestFile);
            });
          });
          const fileCount = majorFiles.length;
          const status = this.computeStatusFromFiles(majorFiles);
          return {
            formId: doc.formId,
            name: doc.name,
            referenceId: doc.referenceId,
            firstName: doc.firstName || '',
            uploadedFiles: [],
            fileCount,
            status
          };
        });
        const formGroups = this.documents.map(doc =>
          this.fb.group({
            id: [doc.formId],
            name: [doc.name],
            uploadedFiles: [doc.uploadedFiles],
            fileCount: [doc.fileCount],
            status: [doc.status]
          })
        );
        this.AccreditationKwtForm.setControl('uploadDocuments', this.fb.array(formGroups));
        this.checkCanSave();
      },
      error: err => {
        console.error('Failed to load documents', err);
      },
      complete: () => {
        this.isLoader = false;
      }
    });
  }


  get uploadDocumentsArray() {
    return this.AccreditationKwtForm.get('uploadDocuments') as FormArray;
  }

  getFileCount(index: number): number {
    const group = this.uploadDocumentsArray.at(index) as FormGroup;
    return group.get('fileCount')?.value || 0;
  }

  computeStatusFromFiles(files: any[]): string {
    if (!files || files.length === 0) {
      return 'Pending';
    }

    const statuses = files.map(f => (f.status || '').toLowerCase());

    if (statuses.every(s => s === 'approve' || s === 'approved')) {
      return 'Approved';
    } else if (statuses.every(s => s === 'reject' || s === 'rejected')) {
      return 'Rejected';
    } else {
      return 'Under review';
    }
  }

  extractFileName(fullUrl: string): string {
    return fullUrl.split('/').pop() || fullUrl;
  }

  onUpload(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const formGroup = this.documentsArray.at(index) as FormGroup;
    const control = formGroup.get('uploadedFiles');
    const currentFiles = control?.value ?? [];

    const newFiles = Array.from(input.files).map(file => ({
      name: file.name,
      size: file.size,
      file,
      saved: false
    }));

    const updatedFiles = [
      ...currentFiles,
      ...newFiles.filter(nf =>
        !currentFiles.some((cf: { name: string; size: number }) => cf.name === nf.name && cf.size === nf.size)
      )
    ];

    control?.setValue(updatedFiles);
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.cdr.detectChanges();
    this.checkCanSave();
    input.value = '';
  }

  removeUploadedFile(documentIndex: number, fileIndex: number): void {
    const documentGroup = this.documentsArray.at(documentIndex) as FormGroup;
    const currentFiles = documentGroup.get('uploadedFiles')?.value || [];
    const removedFile = currentFiles[fileIndex];

    if (removedFile?.id) {
      this.referenceService.deleteUploadedFile(removedFile.id).subscribe({
        next: () => {
          currentFiles.splice(fileIndex, 1);
          documentGroup.get('uploadedFiles')?.setValue(currentFiles);
          this.checkCanSave();
        },
        error: err => console.error('Failed to delete file from DB:', err)
      });
    } else {
      currentFiles.splice(fileIndex, 1);
      documentGroup.get('uploadedFiles')?.setValue(currentFiles);
      this.checkCanSave();
    }
  }

  checkCanSave(): void {
    const docs = this.AccreditationKwtForm?.value?.uploadDocuments ?? [];
    this.canSave = docs.some((doc: any) => doc.uploadedFiles && doc.uploadedFiles.length > 0);
  }

  onView(formId: number): void {
    const dialogRef = this.dialog.open(DocumentGridDialogComponent, {
      width: '1300px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        formId: formId,              // variable
        canEdit: this.tiles.canEdit, // boolean
        canView: this.tiles.canView,    // string
        canDelete: this.tiles.canDelete,
        admin: this.tiles.isAdmin,    // string
        reviewer: this.tiles.isReviewer,    // string
      },
      direction: this.layoutService.selectedLanguage.dir,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((overallStatus: 'Approve' | 'Rejected' | 'Under review' | 'Pending') => {
      if (overallStatus) {
        const index = this.documentsArray.controls.findIndex(ctrl => ctrl.get('id')?.value === formId);
        if (index !== -1) {
          this.documentsArray.at(index).patchValue({ status: overallStatus });
        }
      }
      this.ngOnInit();
    });
  }

  onDownload(file: UploadedFile): void {
    if (file.url) {
      window.open(file.url, '_blank');
    } else {
      alert('This file is not available for download.');
    }
  }

  public getUserTiles(): void {
    if (this.authenticationService.roles?.includes('SysAdmin')) {
      this.tiles = {
        active: true,
        canDelete: true,
        canEdit: true,
        canView: true,
        id: 1,
        isAdmin: true,
        isReviewer: true,
        tileId: 0,
        userId: '0'
      };
    }
    else {
      this.authHttpService.getUserInfo().subscribe({
        next: (result) => {
          if (result) {
            const userId = result.sub;

            this.authHttpService.getUserTiles(userId).subscribe({
              next: (tiles: any) => {
                this.tiles = tiles.filter((x: { id: any; }) => x.id === this.matchedTile.id);
                this.tiles = this.tiles[0];
              },
              error: (error) => {
                this.logService.error('Error fetching user tiles', error);
                this.loading = false;
              }
            });
          } else {
            this.logService.warn('User info not found.');
            this.loading = false;
          }
        },
        error: (error) => {
          this.logService.error('Error fetching user info', error);
          this.loading = false;
        }
      });
    }
  }
}
